import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

const DEFAULT_PROFILE = {
  uid: null,
  email: null,
  roles: [],
  permissions: {
    sellerPrepApproval: false,
    tradeIssueManagement: false,
    inspectionReview: false,
    pickupManagement: false,
    deliveryManagement: false,
    roleManagement: false,

    // Finance reporting permission, optional.
    // Finance dashboard still requires roles: ["admin", "finance"].
    financeReporting: false,
  },
  staffProfile: {
    inspectorType: null,
    partnerId: null,
    storeId: null,
  },
  isActive: false,
};

const normalizeRole = (role) => String(role || "").trim().toLowerCase();

const normalizeRoles = (roles) => {
  if (!Array.isArray(roles)) return [];

  return Array.from(
    new Set(
      roles
        .map(normalizeRole)
        .filter(Boolean)
    )
  );
};

export default function useAccessProfile() {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [loading, setLoading] = useState(true);
  const [exists, setExists] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) {
      setProfile(DEFAULT_PROFILE);
      setExists(false);
      setLoading(false);
      return;
    }

    setLoading(true);

    const ref = doc(db, "users", currentUser.uid);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setProfile({
            ...DEFAULT_PROFILE,
            uid: currentUser.uid,
            email: currentUser.email || null,
          });
          setExists(false);
          setLoading(false);
          return;
        }

        const data = snap.data() || {};
        const roles = normalizeRoles(data.roles);

        setProfile({
          ...DEFAULT_PROFILE,
          ...data,
          uid: data.uid || currentUser.uid,
          email: data.email || currentUser.email || null,
          roles,
          permissions: {
            ...DEFAULT_PROFILE.permissions,
            ...(data.permissions || {}),
          },
          staffProfile: {
            ...DEFAULT_PROFILE.staffProfile,
            ...(data.staffProfile || {}),
          },
          isActive: data.isActive !== false,
        });

        setExists(true);
        setLoading(false);
      },
      (error) => {
        console.error("useAccessProfile snapshot error:", error);

        setProfile({
          ...DEFAULT_PROFILE,
          uid: currentUser.uid,
          email: currentUser.email || null,
        });
        setExists(false);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [currentUser]);

  const value = useMemo(() => {
    const roles = profile.roles || [];
    const permissions = profile.permissions || {};

    const isAdmin = roles.includes("admin");
    const isFinance = roles.includes("finance");

    return {
      profile,
      loading,
      exists,

      isAdmin,
      isFinance,

      // Finance must be admin first, then finance.
      canViewFinance: isAdmin && isFinance,

      canManageRoles: Boolean(isAdmin && permissions.roleManagement),
      canViewFinanceReports: Boolean(isAdmin && isFinance),
    };
  }, [profile, loading, exists]);

  return value;
}